"use client"

import { useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { type Editor } from "@tiptap/react"

// --- Hooks ---
import { useTiptapEditor } from "src/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "src/tiptap/hooks/use-is-breakpoint"

// --- Lib ---
import { isExtensionAvailable } from "src/tiptap/lib/tiptap-utils"

// --- Icons ---
import { ImagePlusIcon } from "src/tiptap/components/tiptap-icons/image-plus-icon"

export const IMAGE_UPLOAD_SHORTCUT_KEY = "mod+shift+i"

export interface UseImageUploadConfig {
  editor?: Editor | null
  hideWhenUnavailable?: boolean
  isUploading?: boolean
  onInserted?: () => void
}

export const canInsertImageUpload = (editor: Editor | null): boolean => {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, "imageUpload")) return false

  return editor.can().insertContent({ type: "imageUpload" })
}

export const isImageUploadActive = (editor: Editor | null): boolean => {
  if (!editor || !editor.isEditable) return false
  return editor.isActive("imageUpload")
}

export const insertImageUpload = (editor: Editor | null): boolean => {
  if (!canInsertImageUpload(editor)) return false

  return Boolean(editor?.chain().focus().insertContent({ type: "imageUpload" }).run())
}

export const shouldShowButton = ({
  editor,
  hideWhenUnavailable,
}: {
  editor: Editor | null
  hideWhenUnavailable: boolean
}): boolean => {
  if (!editor || !editor.isEditable) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isActive("code")) {
    return canInsertImageUpload(editor)
  }

  return true
}

export const useImageUpload = (config?: UseImageUploadConfig) => {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    isUploading = false,
    onInserted,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isVisible, setIsVisible] = useState<boolean>(true)
  const canInsert = canInsertImageUpload(editor) && !isUploading
  const isActive = isImageUploadActive(editor)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    }
  }, [editor, hideWhenUnavailable])

  const handleImage = useCallback(() => {
    const inserted = insertImageUpload(editor)

    if (inserted) {
      onInserted?.()
    }

    return inserted
  }, [editor, onInserted])

  useHotkeys(
    IMAGE_UPLOAD_SHORTCUT_KEY,
    (event) => {
      event.preventDefault()
      if (canInsert) {
        handleImage()
      }
    },
    {
      enabled: isVisible && canInsert,
      enableOnContentEditable: !isMobile,
      enableOnFormTags: true,
    }
  )

  return {
    isVisible,
    isActive,
    handleImage,
    canInsert,
    label: "Add image",
    shortcutKeys: IMAGE_UPLOAD_SHORTCUT_KEY,
    Icon: ImagePlusIcon,
  }
}
